import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import type {
  ScheduleMatchup,
  CompSheetResponse,
  CompPlayer,
  MatchResultInput,
  AnalysisResponse,
  WinProbModel,
  RaceCell,
} from '@/types/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

type Phase = 'schedule' | 'match';

interface MatchState {
  matchNumber: number; // 1, 2, or 3
  decided: MatchResultInput[];
  opponentPutupId: string | null;
}

const WIN_PROB_COLORS = {
  strong: 'text-emerald-600 bg-emerald-50',
  slight: 'text-blue-600 bg-blue-50',
  even: 'text-slate-600 bg-slate-50',
  unfavorable: 'text-amber-600 bg-amber-50',
  bad: 'text-red-600 bg-red-50',
};

function probColor(prob: number, perspective: 'home' | 'away') {
  const p = perspective === 'home' ? prob : 1 - prob;
  if (p >= 0.65) return WIN_PROB_COLORS.strong;
  if (p >= 0.55) return WIN_PROB_COLORS.slight;
  if (p >= 0.45) return WIN_PROB_COLORS.even;
  if (p >= 0.35) return WIN_PROB_COLORS.unfavorable;
  return WIN_PROB_COLORS.bad;
}

export default function MatchupPage() {
  const { divisionId } = useParams<{ divisionId: string }>();
  const [phase, setPhase] = useState<Phase>('schedule');
  const [selectedMatchup, setSelectedMatchup] = useState<ScheduleMatchup | null>(null);
  const [userTeam, setUserTeam] = useState<'home' | 'away'>('away');
  const [selectedOwn, setSelectedOwn] = useState<Set<string>>(new Set());
  const [selectedOpp, setSelectedOpp] = useState<Set<string>>(new Set());
  const [matchState, setMatchState] = useState<MatchState>({
    matchNumber: 1, decided: [], opponentPutupId: null,
  });
  const [analysisCache, setAnalysisCache] = useState<AnalysisResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showProbMatrix, setShowProbMatrix] = useState(false);

  // Fetch schedule
  const { data: schedule, isLoading: schedLoading } = useQuery({
    queryKey: ['schedule', divisionId],
    queryFn: () => apiService.getSchedule(divisionId!),
    enabled: !!divisionId,
  });

  // Fetch comp sheet when matchup is selected
  const { data: compSheet, isLoading: compLoading } = useQuery({
    queryKey: ['comp-sheet', divisionId, selectedMatchup?.home_team_id, selectedMatchup?.away_team_id],
    queryFn: () => apiService.getCompSheet(
      divisionId!,
      selectedMatchup!.home_team_id!,
      selectedMatchup!.away_team_id!,
      selectedMatchup!.week,
      'Thursday',
    ),
    enabled: !!selectedMatchup?.home_team_id && !!selectedMatchup?.away_team_id,
  });

  // Fetch probability matrix independently (not tied to analysis cache)
  const { data: probMatrix } = useQuery({
    queryKey: ['prob-matrix', divisionId, selectedMatchup?.home_team_id, selectedMatchup?.away_team_id],
    queryFn: () => apiService.getProbabilityMatrix(
      divisionId!,
      selectedMatchup!.home_team_id!,
      selectedMatchup!.away_team_id!,
      selectedMatchup!.week,
      'Thursday',
    ),
    enabled: !!selectedMatchup?.home_team_id && !!selectedMatchup?.away_team_id && showProbMatrix,
  });

  // Group matchups by week
  const weekGroups = useMemo(() => {
    if (!schedule) return [];
    const groups: Record<number, { week: number; date: string; matchups: ScheduleMatchup[] }> = {};
    for (const m of schedule.matchups) {
      if (!groups[m.week]) groups[m.week] = { week: m.week, date: m.date, matchups: [] };
      groups[m.week].matchups.push(m);
    }
    return Object.values(groups).sort((a, b) => a.week - b.week);
  }, [schedule]);

  // Derived helpers
  const ownPlayers = useMemo(() => {
    if (!compSheet) return [];
    return userTeam === 'home' ? compSheet.home_players : compSheet.away_players;
  }, [compSheet, userTeam]);

  const oppPlayers = useMemo(() => {
    if (!compSheet) return [];
    return userTeam === 'home' ? compSheet.away_players : compSheet.home_players;
  }, [compSheet, userTeam]);

  // Auto-select all players when comp sheet loads or team switches
  useEffect(() => {
    if (oppPlayers.length > 0) {
      setSelectedOpp(new Set(oppPlayers.map(p => p.player_id)));
    }
    if (ownPlayers.length > 0) {
      setSelectedOwn(new Set(ownPlayers.map(p => p.player_id)));
    }
  }, [oppPlayers, ownPlayers]);

  const usedOwn = useMemo(() => {
    const ids = new Set<string>();
    for (const m of matchState.decided) {
      ids.add(userTeam === 'home' ? m.home_player_id : m.away_player_id);
    }
    return ids;
  }, [matchState.decided, userTeam]);

  const usedOpp = useMemo(() => {
    const ids = new Set<string>();
    for (const m of matchState.decided) {
      ids.add(userTeam === 'home' ? m.away_player_id : m.home_player_id);
    }
    return ids;
  }, [matchState.decided, userTeam]);

  const availableOwn = useMemo(() =>
    [...selectedOwn].filter(id => !usedOwn.has(id)),
    [selectedOwn, usedOwn]
  );

  const availableOpp = useMemo(() =>
    [...selectedOpp].filter(id => !usedOpp.has(id)),
    [selectedOpp, usedOpp]
  );

  // Select a matchup from the schedule → go straight to match
  const handleSelectMatchup = useCallback((m: ScheduleMatchup) => {
    setSelectedMatchup(m);
    setPhase('match');
    setMatchState({ matchNumber: 1, decided: [], opponentPutupId: null });
    setAnalysisCache(null);
  }, []);

  // Run analysis
  const runAnalysis = useCallback(async (putupId?: string, whoPutsUp?: 'user' | 'opponent') => {
    if (!compSheet || !selectedMatchup || !divisionId) return;
    setIsAnalyzing(true);
    try {
      const result = await apiService.analyzeMatchup({
        division_id: divisionId,
        home_team_id: selectedMatchup.home_team_id!,
        away_team_id: selectedMatchup.away_team_id!,
        user_team: userTeam,
        selected_own: [...selectedOwn],
        selected_opp: [...selectedOpp],
        matches_decided: matchState.decided,
        opponent_putup_id: putupId || matchState.opponentPutupId || undefined,
        who_puts_up: whoPutsUp,
        week_number: selectedMatchup.week,
        week_day: 'Thursday',
      });
      setAnalysisCache(result);
    } catch (e) {
      console.error('Analysis failed:', e);
    } finally {
      setIsAnalyzing(false);
    }
  }, [compSheet, selectedMatchup, divisionId, userTeam, selectedOwn, selectedOpp, matchState]);

  // Record a decided match
  const decideMatch = useCallback((homeId: string, awayId: string, winner: 'home' | 'away' | null) => {
    setMatchState(prev => {
      const newDecided = [...prev.decided, { home_player_id: homeId, away_player_id: awayId, winner }];
      return {
        matchNumber: prev.matchNumber + 1,
        decided: newDecided,
        opponentPutupId: null,
      };
    });
    // Run analysis with updated state after a short delay
    setTimeout(() => runAnalysis(), 100);
  }, [runAnalysis]);

  // Set opponent's put-up
  const setOpponentPutup = useCallback((playerId: string) => {
    setMatchState(prev => ({ ...prev, opponentPutupId: playerId }));
    runAnalysis(playerId);
  }, [runAnalysis]);

  // Reset the match
  const resetMatch = useCallback(() => {
    setMatchState({ matchNumber: 1, decided: [], opponentPutupId: null });
    setAnalysisCache(null);
    runAnalysis();
  }, [runAnalysis]);

  const findPlayer = useCallback((id: string, players: CompPlayer[]) =>
    players.find(p => p.player_id === id), []);

  const getRace = useCallback((homeId: string, awayId: string): RaceCell | null => {
    return compSheet?.race_grid[homeId]?.[awayId] || null;
  }, [compSheet]);

  // ============ RENDER ============

  if (schedLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" message="Loading schedule..." />
      </div>
    );
  }

  // === SCHEDULE PHASE ===
  if (phase === 'schedule') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to={divisionId ? `/division/${divisionId}` : '/'} className="text-slate-400 hover:text-slate-600">
            ← Rankings
          </Link>
          <h1 className="text-2xl font-bold text-slate-800">
            Matchup Optimizer
          </h1>
          {schedule && (
            <span className="text-sm text-slate-400">#{divisionId} · {schedule.league_name}</span>
          )}
        </div>

        {/* Week list */}
        <div className="space-y-4">
          {weekGroups.map(({ week, date, matchups }) => {
            const allPlayed = matchups.every(m => m.is_played || m.is_bye);
            const isCurrent = week === schedule?.current_week;

            return (
              <div
                key={week}
                className={`rounded-xl border p-4 ${
                  isCurrent ? 'border-blue-300 bg-blue-50/30 ring-1 ring-blue-200' :
                  allPlayed ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-700">Week {week}</span>
                    <span className="text-sm text-slate-400">{date}</span>
                    {isCurrent && (
                      <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  {matchups.filter(m => !m.is_bye).map((m, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                        m.is_played ? 'bg-white/60' :
                        m.comp_sheet_url ? 'bg-white cursor-pointer hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors' :
                        'bg-white/80'
                      }`}
                      onClick={() => m.home_team_id && m.away_team_id && handleSelectMatchup(m)}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <span className={`font-medium ${m.is_played ? 'text-slate-400' : 'text-slate-700'}`}>
                          {m.home_team}
                        </span>
                        <span className="text-slate-300 text-xs">vs</span>
                        <span className={`font-medium ${m.is_played ? 'text-slate-400' : 'text-slate-700'}`}>
                          {m.away_team}
                        </span>
                      </div>
                      {m.is_played && m.score_home != null ? (
                        <span className="text-xs font-mono text-slate-400">
                          {m.score_home} - {m.score_away}
                        </span>
                      ) : m.home_team_id ? (
                        <span className="text-xs text-blue-500 font-medium">
                          Analyze →
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300">Upcoming</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // === MATCH PHASE ===
  if (compLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" message="Loading comp sheet..." />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setPhase('schedule')} className="text-slate-400 hover:text-slate-600">
            ← Schedule
          </button>
          <h1 className="text-xl font-bold text-slate-800">
            {compSheet?.home_team_name} vs {compSheet?.away_team_name}
          </h1>
          <span className="text-sm text-slate-400">Wk {selectedMatchup?.week}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowProbMatrix(!showProbMatrix)}
            className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-100"
          >
            {showProbMatrix ? 'Hide' : 'Show'} Prob Matrix
          </button>
          <button
            onClick={resetMatch}
            className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Team picker */}
      {compSheet && (
        <div className="flex items-center gap-2 mb-4 text-sm">
          <span className="text-slate-400">Your team:</span>
          <button
            onClick={() => { setUserTeam('home'); resetMatch(); }}
            className={`px-3 py-1 rounded-lg font-medium transition-colors ${
              userTeam === 'home' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {compSheet.home_team_name}
          </button>
          <button
            onClick={() => { setUserTeam('away'); resetMatch(); }}
            className={`px-3 py-1 rounded-lg font-medium transition-colors ${
              userTeam === 'away' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {compSheet.away_team_name}
          </button>
        </div>
      )}

      {/* Match progress bar */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map(n => {
          const decided = matchState.decided[n - 1];
          const isCurrent = matchState.matchNumber === n;
          return (
            <div
              key={n}
              className={`flex-1 rounded-lg p-3 border-2 transition-colors ${
                decided
                  ? decided.winner === userTeam
                    ? 'border-emerald-200 bg-emerald-50'
                    : decided.winner
                      ? 'border-red-200 bg-red-50'
                      : 'border-amber-200 bg-amber-50'
                  : isCurrent ? 'border-blue-300 bg-blue-50' :
                'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${
                  decided
                    ? decided.winner === userTeam ? 'text-emerald-600' : decided.winner ? 'text-red-600' : 'text-amber-600'
                    : isCurrent ? 'text-blue-600' : 'text-slate-400'
                }`}>
                  Match {n}
                  {availableOwn.length === 1 && availableOpp.length === 1 && isCurrent ? ' (Last match)' : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  {decided && (
                    <>
                      {/* Won / Lost / Pending toggle buttons */}
                      {[
                        { label: 'W', value: userTeam, color: 'emerald' },
                        { label: 'L', value: userTeam === 'home' ? 'away' : 'home', color: 'red' },
                        { label: '?', value: null, color: 'amber' },
                      ].map(opt => {
                        const isActive = decided.winner === opt.value;
                        const colorMap: Record<string, string> = {
                          emerald: isActive ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-600 border border-emerald-200 hover:bg-emerald-50',
                          red: isActive ? 'bg-red-500 text-white' : 'bg-white text-red-500 border border-red-200 hover:bg-red-50',
                          amber: isActive ? 'bg-amber-500 text-white' : 'bg-white text-amber-500 border border-amber-200 hover:bg-amber-50',
                        };
                        return (
                          <button
                            key={opt.label}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMatchState(prev => ({
                                ...prev,
                                decided: prev.decided.map((d, i) =>
                                  i === n - 1 ? { ...d, winner: opt.value as 'home' | 'away' | null } : d
                                ),
                              }));
                            }}
                            className={`w-6 h-6 rounded text-[10px] font-bold leading-none ${colorMap[opt.color]}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                      {/* Rollback button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMatchState({
                            matchNumber: n,
                            decided: matchState.decided.slice(0, n - 1),
                            opponentPutupId: null,
                          });
                          setAnalysisCache(null);
                        }}
                        className="w-6 h-6 rounded text-[10px] text-slate-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center"
                        title={`Undo from Match ${n}`}
                      >↩</button>
                    </>
                  )}
                </div>
              </div>
              {decided && compSheet && (
                <div className="mt-1 text-xs text-slate-500">
                  {findPlayer(decided.home_player_id, compSheet.home_players)?.name}
                  {' vs '}
                  {findPlayer(decided.away_player_id, compSheet.away_players)?.name}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Current match interface */}
      {matchState.matchNumber <= 3 && compSheet && (
        <MatchInterface
          key={matchState.matchNumber}
          matchNumber={matchState.matchNumber}
          userTeam={userTeam}
          compSheet={compSheet}
          availableOwn={availableOwn}
          availableOpp={availableOpp}
          opponentPutupId={matchState.opponentPutupId}
          analysis={analysisCache}
          isAnalyzing={isAnalyzing}
          onOpponentPutup={setOpponentPutup}
          onDecide={decideMatch}
          onChoosePutupMode={(mode) => runAnalysis(undefined, mode)}
          getRace={getRace}
        />
      )}

      {matchState.matchNumber > 3 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">
            {matchState.decided.filter(d => d.winner === userTeam).length >= 2 ? '🎉' : '😔'}
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Match Complete
          </h2>
          <p className="text-slate-500">
            Won {matchState.decided.filter(d => d.winner === userTeam).length} of 3 matches
          </p>
          <button
            onClick={resetMatch}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            Run Again
          </button>
        </div>
      )}

      {/* Probability matrix */}
      {showProbMatrix && probMatrix && compSheet && (
        <ProbabilityMatrix
          matrix={probMatrix}
          compSheet={compSheet}
          userTeam={userTeam}
        />
      )}
    </div>
  );
}


// === SUB-COMPONENTS ===


function MatchInterface({
  matchNumber, userTeam, compSheet, availableOwn, availableOpp,
  opponentPutupId, analysis, isAnalyzing, onOpponentPutup, onDecide, onChoosePutupMode, getRace,
}: {
  matchNumber: number;
  userTeam: 'home' | 'away';
  compSheet: CompSheetResponse;
  availableOwn: string[];
  availableOpp: string[];
  opponentPutupId: string | null;
  analysis: AnalysisResponse | null;
  isAnalyzing: boolean;
  onOpponentPutup: (id: string) => void;
  onDecide: (homeId: string, awayId: string, winner: 'home' | 'away' | null) => void;
  onChoosePutupMode: (mode: 'user' | 'opponent') => void;
  getRace: (homeId: string, awayId: string) => RaceCell | null;
}) {
  const [mode, setMode] = useState<'choose' | 'we_putup' | 'they_putup'>('choose');
  const [ourPutupId, setOurPutupId] = useState<string | null>(null);
  const isForced = availableOwn.length === 1 && availableOpp.length === 1;

  const ownPlayersList = userTeam === 'home' ? compSheet.home_players : compSheet.away_players;
  const oppPlayersList = userTeam === 'home' ? compSheet.away_players : compSheet.home_players;

  const makeIds = (ownId: string, oppId: string) => ({
    homeId: userTeam === 'home' ? ownId : oppId,
    awayId: userTeam === 'home' ? oppId : ownId,
  });

  // Forced match — last remaining players
  if (isForced) {
    const ownId = availableOwn[0];
    const oppId = availableOpp[0];
    const { homeId, awayId } = makeIds(ownId, oppId);
    const ownPlayer = ownPlayersList.find(p => p.player_id === ownId);
    const oppPlayer = oppPlayersList.find(p => p.player_id === oppId);
    const race = getRace(homeId, awayId);
    const forcedPick = analysis?.optimal_counters?.[0];

    return (
      <div className="rounded-xl border-2 border-amber-200 bg-amber-50/30 p-6">
        <h2 className="text-lg font-bold text-amber-700 mb-4">
          Match {matchNumber} — Last Match
        </h2>
        <div className="flex items-center justify-center gap-6">
          <PlayerCard player={ownPlayer!} label="You" accent="blue" />
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-300 mb-1">VS</div>
            {race && (
              <div className="text-sm font-mono font-semibold text-slate-600">
                {race.home_race} - {race.away_race}
              </div>
            )}
            {race && race.h2h_wins != null && race.h2h_losses != null && (
              <div className={`mt-1 text-xs font-medium ${
                race.h2h_wins > race.h2h_losses ? 'text-emerald-600' :
                race.h2h_wins < race.h2h_losses ? 'text-red-500' : 'text-slate-400'
              }`}>H2H {race.h2h_wins}-{race.h2h_losses}</div>
            )}
            {forcedPick && (
              <div className={`mt-2 text-sm font-semibold ${forcedPick.win_prob >= 0.5 ? 'text-emerald-600' : 'text-red-500'}`}>
                {(forcedPick.win_prob * 100).toFixed(0)}% win
              </div>
            )}
          </div>
          <PlayerCard player={oppPlayer!} label="Opponent" accent="red" />
        </div>
        <div className="flex justify-center gap-3 mt-6">
          <button
            onClick={() => onDecide(homeId, awayId, userTeam)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
          >
            Won ✓
          </button>
          <button
            onClick={() => onDecide(homeId, awayId, userTeam === 'home' ? 'away' : 'home')}
            className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600"
          >
            Lost ✗
          </button>
          <button
            onClick={() => onDecide(homeId, awayId, null)}
            className="px-4 py-2 bg-slate-200 text-slate-600 rounded-lg font-medium hover:bg-slate-300"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-blue-200 bg-blue-50/20 p-6">
      <h2 className="text-lg font-bold text-blue-700 mb-1">Match {matchNumber}</h2>

      {isAnalyzing && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" message="Calculating optimal strategy..." />
        </div>
      )}

      {/* Step 1: Choose who puts up */}
      {!isAnalyzing && mode === 'choose' && !opponentPutupId && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Who is putting up a player?</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setMode('we_putup'); onChoosePutupMode('user'); }}
              className="p-4 rounded-xl border-2 border-blue-200 bg-white hover:bg-blue-50 hover:border-blue-400 transition-colors text-center"
            >
              <div className="font-semibold text-blue-700">We put up</div>
              <div className="text-xs text-slate-400 mt-1">You reveal a player, they counter</div>
            </button>
            <button
              onClick={() => { setMode('they_putup'); onChoosePutupMode('opponent'); }}
              className="p-4 rounded-xl border-2 border-red-200 bg-white hover:bg-red-50 hover:border-red-400 transition-colors text-center"
            >
              <div className="font-semibold text-red-700">They put up</div>
              <div className="text-xs text-slate-400 mt-1">They reveal a player, you counter</div>
            </button>
          </div>
        </div>
      )}

      {/* We put up: select our player, then they counter */}
      {!isAnalyzing && mode === 'we_putup' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {!ourPutupId ? 'Select your player to put up:' : 'Who did they counter with?'}
            </p>
            <button onClick={() => { setMode('choose'); setOurPutupId(null); }} className="text-xs text-slate-400 hover:text-slate-600">
              ← Back
            </button>
          </div>

          {!ourPutupId && (
            <div className="space-y-2">
              {analysis?.optimal_putup && (
                <div className="p-3 rounded-lg bg-blue-100/60 border border-blue-200 mb-2">
                  <div className="text-xs font-semibold text-blue-500 uppercase tracking-wider mb-1">
                    Recommended Put-Up
                  </div>
                  <span className="font-bold text-slate-800">{analysis.optimal_putup.player_name}</span>
                  <span className="text-xs text-slate-500 ml-2">{analysis.optimal_putup.reasoning}</span>
                </div>
              )}
              <div className="grid grid-cols-1 gap-2">
              {availableOwn.map(ownId => {
                const player = ownPlayersList.find(p => p.player_id === ownId);
                if (!player) return null;
                const isRecommended = ownId === analysis?.optimal_putup?.player_id;
                return (
                  <div
                    key={ownId}
                    onClick={() => setOurPutupId(ownId)}
                    className={`flex items-center justify-between rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                      isRecommended
                        ? 'bg-emerald-50/50 border-2 border-emerald-300 hover:border-emerald-400'
                        : 'bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isRecommended && <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full font-semibold">★</span>}
                      <span className="font-medium text-slate-700">{player.name}</span>
                      {player.is_captain && <span className="text-xs text-slate-400 ml-1">(c)</span>}
                    </div>
                    <span className="text-xs text-slate-400">{player.csr} CSR · {player.win_pct}%</span>
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {ourPutupId && (
            <>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold text-blue-500 uppercase tracking-wider">You put up:</span>
                    <span className="ml-2 font-bold text-slate-800">
                      {ownPlayersList.find(p => p.player_id === ourPutupId)?.name}
                    </span>
                  </div>
                  <button onClick={() => setOurPutupId(null)} className="text-xs text-slate-400 hover:text-slate-600">Change</button>
                </div>
              </div>
              <p className="text-sm text-slate-500">Who did they counter with?</p>
              <div className="grid grid-cols-1 gap-2">
                {availableOpp
                  .map(oppId => {
                    const player = oppPlayersList.find(p => p.player_id === oppId);
                    if (!player) return null;
                    const { homeId, awayId } = makeIds(ourPutupId, oppId);
                    const race = getRace(homeId, awayId);
                    const prob = analysis?.probability_matrix?.find(
                      m => m.home_player_id === homeId && m.away_player_id === awayId
                    );
                    const winProb = prob
                      ? (userTeam === 'home' ? prob.home_win_prob : prob.away_win_prob)
                      : null;
                    return { oppId, player, homeId, awayId, race, winProb };
                  })
                  .filter(Boolean)
                  .sort((a, b) => (b!.winProb ?? 0) - (a!.winProb ?? 0))
                  .map(item => {
                    const { oppId, player, homeId, awayId, race, winProb } = item!;
                    return (
                      <div
                        key={oppId}
                        onClick={() => {
                          onDecide(homeId, awayId, null);
                          setMode('choose');
                          setOurPutupId(null);
                        }}
                        className="flex items-center justify-between rounded-lg px-4 py-3 bg-white border border-slate-200 cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                      >
                        <div>
                          <span className="font-medium text-slate-700">{player.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{player.csr} CSR</span>
                          {race && <span className="text-xs font-mono text-slate-400 ml-2">Race {race.home_race}-{race.away_race}</span>}
                          {race && race.h2h_wins != null && race.h2h_losses != null && (
                            <span className={`text-xs ml-2 ${
                              race.h2h_wins > race.h2h_losses ? 'text-emerald-600' :
                              race.h2h_wins < race.h2h_losses ? 'text-red-500' : 'text-slate-400'
                            }`}>H2H {race.h2h_wins}-{race.h2h_losses}</span>
                          )}
                        </div>
                        {winProb != null && (
                          <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                            winProb >= 0.55 ? 'bg-emerald-100 text-emerald-700' :
                            winProb >= 0.45 ? 'bg-slate-100 text-slate-700' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {(winProb * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      )}

      {/* They put up: select their player, then we counter */}
      {!isAnalyzing && (mode === 'they_putup' || opponentPutupId) && (
        <div className="space-y-4">
          {!opponentPutupId && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">Who did the opponent put up?</p>
                <button onClick={() => setMode('choose')} className="text-xs text-slate-400 hover:text-slate-600">
                  ← Back
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {availableOpp.map(oppId => {
                  const player = oppPlayersList.find(p => p.player_id === oppId);
                  if (!player) return null;
                  return (
                    <div
                      key={oppId}
                      onClick={() => onOpponentPutup(oppId)}
                      className="flex items-center justify-between rounded-lg px-4 py-3 bg-white border border-slate-200 cursor-pointer hover:border-red-300 hover:bg-red-50/30 transition-colors"
                    >
                      <div>
                        <span className="font-medium text-slate-700">{player.name}</span>
                        {player.is_captain && <span className="text-xs text-slate-400 ml-1">(c)</span>}
                      </div>
                      <span className="text-xs text-slate-400">{player.csr} CSR · {player.win_pct}%</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {opponentPutupId && (
            <>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <span className="text-xs font-semibold text-red-500 uppercase tracking-wider">They put up:</span>
                <span className="ml-2 font-bold text-slate-800">
                  {oppPlayersList.find(p => p.player_id === opponentPutupId)?.name}
                </span>
              </div>
              <div className="text-sm font-medium text-slate-600 mb-2">
                Your counter options (best first):
              </div>
              {analysis?.optimal_counters?.map((pick, i) => {
                const player = ownPlayersList.find(p => p.player_id === pick.player_id);
                const { homeId, awayId } = makeIds(pick.player_id, opponentPutupId);
                const race = getRace(homeId, awayId);
                return (
                  <div key={pick.player_id} className={`rounded-lg border p-4 ${
                    i === 0 ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200 bg-white'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {i === 0 && (
                          <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full font-semibold">BEST</span>
                        )}
                        <span className="font-bold text-slate-800">{pick.player_name}</span>
                        {player && <span className="text-xs text-slate-400">{player.csr} CSR</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        {race && race.h2h_wins != null && race.h2h_losses != null && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            race.h2h_wins > race.h2h_losses ? 'bg-emerald-50 text-emerald-600' :
                            race.h2h_wins < race.h2h_losses ? 'bg-red-50 text-red-500' :
                            'bg-slate-50 text-slate-500'
                          }`}>
                            H2H {race.h2h_wins}-{race.h2h_losses}
                          </span>
                        )}
                        {race && <span className="text-xs font-mono text-slate-500">Race {race.home_race}-{race.away_race}</span>}
                        <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                          pick.win_prob >= 0.55 ? 'bg-emerald-100 text-emerald-700' :
                          pick.win_prob >= 0.45 ? 'bg-slate-100 text-slate-700' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {(pick.win_prob * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    {i === 0 && (
                      <p className="text-[11px] text-emerald-600/70 mb-1 -mt-1">
                        Best overall pick — considers win probability here plus optimal matchups for remaining matches
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mb-3">{pick.reasoning}</p>
                    <button
                      onClick={() => onDecide(homeId, awayId, null)}
                      className="px-4 py-1.5 bg-blue-600 text-white text-xs rounded-lg font-medium hover:bg-blue-700"
                    >Pick</button>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Methodology explanation */}
      <details className="mt-4 text-xs text-slate-400">
        <summary className="cursor-pointer hover:text-slate-600 select-none">
          How recommendations work
        </summary>
        <div className="mt-2 p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2 text-slate-500">
          <p>
            <strong className="text-slate-600">Win %</strong> — Probability of winning this individual match,
            calculated from race handicaps (negative binomial model), session form (recent win rate),
            and head-to-head record.
          </p>
          <p>
            <strong className="text-slate-600">★ Recommended / BEST</strong> — Uses minimax strategy across all 3 matches.
            Considers not just this match, but how remaining players match up in future rounds.
            A slightly lower win% pick may be recommended if it leaves better matchups for later.
          </p>
          <p>
            <strong className="text-slate-600">Score</strong> — Net expected advantage: positive means you're favored.
            Calculated as (win probability − loss probability) summed across remaining matches.
          </p>
          <p>
            <strong className="text-slate-600">H2H</strong> — Head-to-head record between these two players
            in this division/session. Factors into win probability with 10% weight when ≥2 matches exist.
          </p>
        </div>
      </details>
    </div>
  );
}


function PlayerCard({ player, label, accent }: {
  player: CompPlayer;
  label: string;
  accent: 'blue' | 'red';
}) {
  return (
    <div className={`rounded-xl border-2 p-4 text-center min-w-[140px] ${
      accent === 'blue' ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50'
    }`}>
      <div className="text-xs font-semibold text-slate-400 uppercase mb-1">{label}</div>
      <div className="font-bold text-slate-800">{player.name}</div>
      <div className="text-sm text-slate-500 mt-1">
        {player.csr} CSR · {player.win_pct}%
      </div>
    </div>
  );
}


function ProbabilityMatrix({ matrix, compSheet, userTeam }: {
  matrix: WinProbModel[];
  compSheet: CompSheetResponse;
  userTeam: 'home' | 'away';
}) {
  return (
    <div className="mt-6 rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-sm text-slate-600 mb-3">Full Win Probability Matrix</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left p-2 text-slate-400">Home ↓ / Away →</th>
              {compSheet.away_players.map(p => (
                <th key={p.player_id} className="p-2 text-center text-slate-600">
                  {p.name.split(' ')[0]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {compSheet.home_players.map(hp => (
              <tr key={hp.player_id}>
                <td className="p-2 font-medium text-slate-600">{hp.name.split(' ')[0]}</td>
                {compSheet.away_players.map(ap => {
                  const prob = matrix.find(
                    m => m.home_player_id === hp.player_id && m.away_player_id === ap.player_id
                  );
                  if (!prob) return <td key={ap.player_id} className="p-2 text-center text-slate-300">—</td>;
                  return (
                    <td key={ap.player_id} className="p-2 text-center">
                      <span className={`inline-block px-2 py-1 rounded font-semibold ${probColor(prob.home_win_prob, userTeam)}`}>
                        {userTeam === 'home'
                          ? `${(prob.home_win_prob * 100).toFixed(0)}%`
                          : `${(prob.away_win_prob * 100).toFixed(0)}%`}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        Percentages show win probability from {userTeam === 'home' ? 'home' : 'away'} perspective.
        Green = favorable, Red = unfavorable.
      </p>
    </div>
  );
}
